import type { CaptureDiagnosis, PhraseFeedback, TimingFeedback } from '@tunasong/music-lib'
import type { FC } from 'react'
import { PitchFeedbackPanel } from '../pitch-training/pitch-feedback-panel.js'
import { TimingFeedbackPanel } from './timing-feedback-panel.js'
import styles from './melody-trainer-feedback.module.css'

/**
 * The two layers of an attempt's feedback, in the order a player can use them.
 *
 * Pitch first: it is the layer that decides which note is which, and a timing verdict about a note
 * the player got wrong is a verdict about a different note. `PitchFeedbackPanel` is reused
 * unchanged - the Pitch Trainer's §12 copy already says everything there is to say about a sung
 * note, and a second panel saying it differently would be two tools disagreeing about one voice.
 *
 * When nothing was heard, the timing panel is NOT rendered. An onset error measured against notes
 * nobody played is not a small number, it is a meaningless one, and printing it would be the same
 * class of failure as handing a silent user a score.
 *
 * Spec: docs/superpowers/specs/2026-09-08-melody-plugin-design.md §6.2, §6.4
 */

export interface MelodyTrainerFeedbackProps {
  pitch: PhraseFeedback
  /** Why nothing was heard, when nothing was. Null in MIDI mode, which has no capture. */
  diagnosis: CaptureDiagnosis | null
  /** Null when the aligner paired nothing - there is no timing to report. */
  timing: TimingFeedback | null
  onRetry: () => void
  onNext: () => void
}

export const MelodyTrainerFeedback: FC<MelodyTrainerFeedbackProps> = props => {
  const { pitch, diagnosis, timing, onRetry, onNext } = props

  return (
    <div className={styles.feedback}>
      <PitchFeedbackPanel feedback={pitch} diagnosis={diagnosis} onRetry={onRetry} onNext={onNext} />
      {pitch.nothingHeard ? null : <TimingFeedbackPanel feedback={timing} />}
    </div>
  )
}
